import { ReactElement, useState } from "react";

import { CountButton } from "@/Common/Components/Buttons/CountButton";
import { NotesPopup } from "@/Common/Components/Modals/NotesPopup";
import { NoteListItem } from "@/Common/Components/NotesList/NotesList";

import styles from "./AddNotesPopupButton.module.scss";

/**
 * The properties for a {@link AddNotesPopupButton} component.
 */
interface AddNotesPopupButtonProps {
    /**
     * The notes to list in the popup.
     */
    notes: NoteListItem[];

    /**
     * A unique ID used to disambiguate when there are multiple popups shown. This is appended to the generic ID.
     */
    itemId: string;

    /**
     * A callback invoked when the note is submitted.
     * @param note The content of the note.
     * @returns Whether the popup should be closed.
     */
    onSubmit(note: string): Promise<boolean>;

    /**
     * Whether to highlight the notes count if there's a least one note.
     */
    textColorImportant?: boolean;

    onCountClick?: (then?: () => void) => void;

    /*
     * Wether the data is loaded outside of component. If undefined, we set to true by default
     */
    isDataLoaded?: boolean;
}

/**
 * A button that opens a popup to add notes on click.
 * @param props The component properties.
 */
function AddNotesPopupButton(props: Readonly<AddNotesPopupButtonProps>): ReactElement {
    const { isDataLoaded = true } = props;
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [popupTopPositionPixels, setPopupTopPositionPixels] = useState<number>();
    const [popupLeftPositionPixels, setPopupLeftPositionPixels] = useState<number>();

    function handleOnCountClicked(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
        const buttonCenterXAxis = mean(event.currentTarget.getBoundingClientRect().right, event.currentTarget.getBoundingClientRect().left);
        const buttonCenterYAxis = mean(event.currentTarget.getBoundingClientRect().top, event.currentTarget.getBoundingClientRect().bottom);
        setPopupLeftPositionPixels(buttonCenterXAxis);
        setPopupTopPositionPixels(buttonCenterYAxis);

        const onLoadNotes = async (): Promise<void> => {
            props.onCountClick?.();
            setIsOpen(true);
        };
        onLoadNotes();
    }

    function handleOnClose(): void {
        setIsOpen(false);
    }

    async function handleOnSubmitNoteClicked(note: string): Promise<void> {
        try {
            setIsLoading(true);
            if (await props.onSubmit(note))
                setIsOpen(false);
        }
        finally {
            setIsLoading(false);
        }
    }

    const notesPopupId = `notes-popup-id-${props.itemId}`;

    return (
        <>
            <CountButton
                count={props.notes.length}
                aria-label={`${props.notes.length}`}
                aria-haspopup="true"
                aria-expanded={isOpen}
                aria-controls={notesPopupId}
                data-cy={`notes-count-link-${props.itemId}`}
                onClick={handleOnCountClicked}
                textClassName={props.textColorImportant && props.notes.length > 0 ? styles.notesPopupImportant : ""}
            />
            {isOpen && isDataLoaded && (
                <NotesPopup
                    notes={props.notes}
                    onSubmitNoteClicked={note => handleOnSubmitNoteClicked(note)}
                    isOpen={isOpen}
                    onClose={handleOnClose}
                    isSubmitDisabled={isLoading}
                    topPositionPx={popupTopPositionPixels}
                    leftPositionPx={popupLeftPositionPixels}
                    popupId={`add-notes-popup-${props.itemId}`}
                    dataCy={`add-notes-popup-${props.itemId}`}
                    notesListDataCy={`notes-list-container-${props.itemId}`}
                    textAreaDataCy={`notes-textarea-${props.itemId}`}
                />
            )}
        </>
    );
}

/**
 * Get the mean of two numbers.
 * @param a The first number for which to get the mean.
 * @param b The second number for which to get the mean.
 */
function mean(a: number, b: number): number {
    return (a + b) / 2;
}

export { AddNotesPopupButton };
